import { useCallback, useState } from 'react';
import { useSWRConfig } from 'swr';
import { useTranslation } from 'react-i18next';
import { AutoHideOnScroll } from '@/components';
import type { UndefinedIdentityCellConfig } from '@/components/IdentityAvatarGrid';
import { CACHE_KEYS } from '@/constants/cache-keys';
import {
    getIdentityActionDescriptors,
    type IdentityActionKind,
} from '@/features/IdentityActions';
import { IdentityFilterPanel } from '@/features/IdentityFilter';
import { SidePanel } from '@/components/SidePanel';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import type { Identity } from '@/types/identity';
import { ReportType } from '@/types/report';
import {
    buildUndefinedIdentityCell,
    encodeIdentityTokens,
    identityToken,
} from '@/utils/identity';
import { countUndefinedEventsByKind } from './utils/countUndefinedEventsByKind';
import { ReportIdentityActionModals } from './components/ReportIdentityActionModals';
import { ReportActionBar } from './components/ReportActionBar';
import { ReportDetailHeader } from './components/ReportDetailHeader';
import { ReportDetailModals } from './components/ReportDetailModals';
import { ReportEventsSection } from './components/ReportEventsSection';
import { ReportInfoBar } from './components/ReportInfoBar';
import { ReportSidebar } from './components/ReportSidebar';
import { useReportActions } from './hooks/useReportActions';
import { useReportBulkRemoval } from './hooks/useReportBulkRemoval';
import { useReportData } from './hooks/useReportData';
import { useReportEventEditing } from './hooks/useReportEventEditing';
import { useReportEventOrdering } from './hooks/useReportEventOrdering';
import { useReportEventRemoval } from './hooks/useReportEventRemoval';
import { useReportExport, type ReportExportFn } from './hooks/useReportExport';
import { useReportOrderMutation } from './hooks/useReportOrderMutation';
import { useReportPipelineStatus } from './hooks/useReportPipelineStatus';
import { useReportEventClipExport } from '@/hooks/useReportEventClipExport';
import { useReportPromptOrdering } from './hooks/useReportPromptOrdering';
import { useReportViewControls } from './hooks/useReportViewControls';
import { useReportZipExport } from './hooks/useReportZipExport';
import { exportReportToDoc } from './utils/exportReportToDoc';
import { exportReportToJson } from './utils/exportReportToJson';
import { exportReportToPdf } from './utils/exportReportToPdf';

interface ReportDetailProps {
    reportId: string;
    onReportNameResolved?: (name: string | undefined) => void;
}

export function ReportDetail({
    reportId,
    onReportNameResolved,
}: ReportDetailProps) {
    const { t } = useTranslation(I18nNamespace.REPORTS);
    const { t: translateCommon } = useTranslation(I18nNamespace.COMMON);
    const { t: translateDirectory } = useTranslation(I18nNamespace.DIRECTORY);
    const { mutate: mutateCache } = useSWRConfig();
    const [scrollContainer, setScrollContainer] =
        useState<HTMLDivElement | null>(null);
    const [addEventModalOpen, setAddEventModalOpen] = useState(false);
    const [addSourceModalOpen, setAddSourceModalOpen] = useState(false);

    const viewControls = useReportViewControls();
    const { report, mutate, isInitialLoading, isExportEnabled } = useReportData(
        {
            reportId,
            minConfidence: viewControls.confidence.min,
            onReportNameResolved,
        },
    );

    const isProcedureReport = report?.type === ReportType.PROCEDURE;
    const { pipelineStatus, error: pipelineStatusError } =
        useReportPipelineStatus(isProcedureReport ? reportId : null);

    const reportActions = useReportActions(reportId, mutate);
    const exporting = useReportExport({
        report,
        groupBy: viewControls.groupBy,
    });
    const zipExport = useReportZipExport({ report });
    const eventClipExport = useReportEventClipExport(reportId);
    const editing = useReportEventEditing(report?.events);
    const removal = useReportEventRemoval(mutate);
    const bulk = useReportBulkRemoval(reportId, mutate);
    const orderMutation = useReportOrderMutation({ report, mutate });
    const ordering = useReportEventOrdering({
        reportId,
        mutate,
        groupBy: viewControls.groupBy,
        sortOption: viewControls.sortOption,
        setSortOption: viewControls.setSortOption,
        orderMutation,
    });
    const promptReordering = useReportPromptOrdering({
        reportId,
        mutate,
        orderMutation,
    });
    const handleReportEventsChanged = useCallback(() => {
        void Promise.all([
            mutate(),
            mutateCache(
                (key) =>
                    Array.isArray(key) && key[0] === CACHE_KEYS.REPORTS_LIST,
                undefined,
                { revalidate: true },
            ),
        ]);
    }, [mutate, mutateCache]);

    const visibleEvents = viewControls.getVisibleEvents(report?.events ?? []);
    const identitySelectionKey =
        encodeIdentityTokens(viewControls.identityTokens) ?? '';

    const identities = report?.identities ?? [];
    const undefinedEventCounts = countUndefinedEventsByKind(
        report?.events ?? [],
    );
    const undefinedIdentityCells: UndefinedIdentityCellConfig[] = (
        ['person', 'vehicle'] as const
    )
        .filter((kind) => undefinedEventCounts[kind] > 0)
        .map((kind) =>
            buildUndefinedIdentityCell({
                kind,
                count: undefinedEventCounts[kind],
                t: translateCommon,
                labelPerKind: true,
            }),
        );

    const sidebarIdentities = isProcedureReport
        ? identities
        : identities.filter((identity) =>
              viewControls.identityTokens.has(identityToken(identity)),
          );
    const sidebarUndefinedIdentityCells = isProcedureReport
        ? undefinedIdentityCells
        : undefinedIdentityCells.filter((cell) =>
              viewControls.identityTokens.has(cell.token),
          );

    const [identityAction, setIdentityAction] = useState<{
        identity: Identity;
        kind: IdentityActionKind;
        requestId: number;
    } | null>(null);

    const buildIdentityMenuItems = (identity: Identity) =>
        getIdentityActionDescriptors({
            entityKind: identity.kind,
            known: identity.known,
            includeDelete: false,
            t: translateDirectory,
        }).map((descriptor) => ({
            key: descriptor.kind,
            label: descriptor.label,
            icon: descriptor.icon,
            onSelect: () =>
                setIdentityAction((previous) => ({
                    identity,
                    kind: descriptor.kind,
                    requestId: (previous?.requestId ?? 0) + 1,
                })),
        }));

    const identityFilterPanel = (
        <IdentityFilterPanel
            identities={identities}
            selectedTokens={viewControls.identityTokens}
            onToggle={viewControls.toggleIdentity}
            onClear={viewControls.clearIdentitySelection}
            undefinedEventCounts={undefinedEventCounts}
            buildMenuItems={buildIdentityMenuItems}
        />
    );

    const visibleSelectedEvents = bulk.isMultiSelectMode
        ? visibleEvents.filter((event) => bulk.selectedIds.has(event.id))
        : [];

    const exportEvents = () =>
        visibleSelectedEvents.length > 0
            ? visibleSelectedEvents
            : visibleEvents;

    const exportDocument = async (exportFn: ReportExportFn) => {
        const narrowed = visibleSelectedEvents.length > 0;
        const success = await exporting.handleExport(exportFn, exportEvents());
        if (success && narrowed) {
            bulk.exitMultiSelectMode();
        }
    };

    const exportZip = async () => {
        const narrowed = visibleSelectedEvents.length > 0;
        const started = await zipExport.startExport(exportEvents());
        if (started && narrowed) {
            bulk.exitMultiSelectMode();
        }
    };

    return (
        <div className="relative h-full min-h-0">
            <SidePanel side="left">
                <ReportSidebar
                    groupBy={viewControls.groupBy}
                    onClearGroupBy={() => viewControls.setGroupBy('none')}
                    confidence={viewControls.confidence}
                    identities={sidebarIdentities}
                    selectedIdentityTokens={viewControls.identityTokens}
                    onToggleIdentity={viewControls.toggleIdentity}
                    onClearIdentitySelection={
                        viewControls.clearIdentitySelection
                    }
                    undefinedIdentityCells={sidebarUndefinedIdentityCells}
                    buildIdentityMenuItems={buildIdentityMenuItems}
                />
            </SidePanel>
            {report && (
                <SidePanel side="right">
                    <ReportInfoBar
                        createdAt={report.createdAt}
                        updatedAt={report.updatedAt}
                    />
                </SidePanel>
            )}
            <div className="h-full flex flex-col relative">
                <AutoHideOnScroll container={scrollContainer} className="top-6">
                    <ReportActionBar
                        identityFilter={identityFilterPanel}
                        isIdentityFilterActive={
                            viewControls.identityTokens.size > 0
                        }
                        searchValue={viewControls.search}
                        onSearchChange={viewControls.setSearch}
                        searchPlaceholder={t('detail.searchPlaceholder')}
                        groupBy={viewControls.groupBy}
                        onGroupByChange={viewControls.setGroupBy}
                        sortOption={viewControls.sortOption}
                        onSortOptionChange={viewControls.setSortOption}
                        confidence={viewControls.confidence}
                        onConfidenceApply={viewControls.setConfidence}
                        onConfidenceClear={viewControls.resetConfidence}
                        onAddEvents={() => setAddEventModalOpen(true)}
                        onEditReport={reportActions.openEditModal}
                        onDeleteReport={reportActions.openDeleteModal}
                        isExportEnabled={isExportEnabled}
                        isExporting={exporting.isExporting}
                        isZipExporting={zipExport.isBusy}
                        zipExportEnabled={zipExport.exportable}
                        onExportPdf={() =>
                            void exportDocument(exportReportToPdf)
                        }
                        onExportDoc={() =>
                            void exportDocument(exportReportToDoc)
                        }
                        onExportJson={() =>
                            void exportDocument(exportReportToJson)
                        }
                        onExportZip={() => void exportZip()}
                        isMultiSelectMode={bulk.isMultiSelectMode}
                        selectedCount={visibleSelectedEvents.length}
                        totalCount={visibleEvents.length}
                        onEnterMultiSelect={bulk.enterMultiSelectMode}
                        onExitMultiSelect={bulk.exitMultiSelectMode}
                        onSelectAll={() => bulk.toggleSelectAll(visibleEvents)}
                        selectionBulkActions={bulk.selectionBulkActions}
                    />
                </AutoHideOnScroll>
                <div
                    ref={setScrollContainer}
                    className="flex-1 overflow-y-auto scrollbar-hide min-h-0 pb-32 px-4 panels:px-8"
                >
                    <div className="w-full max-w-content mx-auto pt-20">
                        <ReportDetailHeader
                            isInitialLoading={isInitialLoading}
                            report={report}
                            isProcedureReport={isProcedureReport}
                            pipelineStatus={pipelineStatus}
                            pipelineStatusError={pipelineStatusError}
                        />
                        <ReportEventsSection
                            isInitialLoading={isInitialLoading}
                            reportId={reportId}
                            report={report}
                            isProcedureReport={isProcedureReport}
                            pipelineStatus={pipelineStatus}
                            visibleEvents={visibleEvents}
                            groupBy={viewControls.groupBy}
                            identitySelectionKey={identitySelectionKey}
                            isMultiSelectMode={bulk.isMultiSelectMode}
                            selectedIds={bulk.selectedIds}
                            onToggleSelect={bulk.toggleSelected}
                            onEditEvent={editing.requestEdit}
                            onRemoveEvent={removal.requestRemove}
                            onExportEvent={eventClipExport.startExport}
                            exportBusy={eventClipExport.busy}
                            onAddEvents={() => setAddEventModalOpen(true)}
                            onAddSource={() => setAddSourceModalOpen(true)}
                            reordering={ordering.reordering}
                            promptReordering={promptReordering}
                        />
                    </div>
                </div>
                <div
                    className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none"
                    style={{
                        background:
                            'linear-gradient(to top, rgb(17, 18, 20) 0%, transparent 100%)',
                    }}
                />
                <ReportDetailModals
                    reportId={reportId}
                    report={report}
                    onReportEventsChanged={handleReportEventsChanged}
                    editReportModalOpen={reportActions.editModalOpen}
                    onEditReportClose={reportActions.closeEditModal}
                    onEditReportSubmit={reportActions.submitEdit}
                    deleteReportModalOpen={reportActions.deleteModalOpen}
                    deleteReportInProgress={reportActions.deleteInProgress}
                    onDeleteReportConfirm={reportActions.confirmDelete}
                    onDeleteReportCancel={reportActions.closeDeleteModal}
                    removeEventModalOpen={
                        removal.removeEventIdPending !== undefined
                    }
                    removeEventInProgress={removal.removeInProgress}
                    onRemoveEventConfirm={removal.confirmRemove}
                    onRemoveEventCancel={removal.cancelRemove}
                    bulkRemoveModalOpen={bulk.bulkRemoveModalOpen}
                    bulkRemoveCount={visibleSelectedEvents.length}
                    bulkRemoveInProgress={bulk.bulkRemoveInProgress}
                    onBulkRemoveConfirm={() =>
                        bulk.confirmBulkRemove(
                            visibleSelectedEvents.map((event) => event.id),
                        )
                    }
                    onBulkRemoveCancel={bulk.closeBulkRemoveModal}
                    addEventModalOpen={addEventModalOpen}
                    onAddEventClose={() => setAddEventModalOpen(false)}
                    addSourceModalOpen={addSourceModalOpen}
                    onAddSourceClose={() => setAddSourceModalOpen(false)}
                    editingEvent={editing.editingEvent}
                    onEditEventClose={editing.closeEdit}
                    reorderConfirmationOpen={ordering.confirmationOpen}
                    reorderInProgress={ordering.isReordering}
                    onReorderConfirm={() =>
                        void ordering.confirmSortedReorder()
                    }
                    onReorderCancel={ordering.cancelSortedReorder}
                />
                {identityAction && (
                    <ReportIdentityActionModals
                        key={identityAction.requestId}
                        identity={identityAction.identity}
                        actionKind={identityAction.kind}
                        onClose={() => setIdentityAction(null)}
                        onChanged={() => void mutate()}
                    />
                )}
            </div>
        </div>
    );
}
